import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary,
  Alert, Autocomplete, Box, Button, Chip, CircularProgress,
  Divider, FormControl, FormControlLabel, IconButton, InputAdornment,
  InputLabel, MenuItem, Paper, Select, Skeleton, Snackbar, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  TextField, Tooltip, Typography,
  Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress,
} from '@mui/material';
import {
  Add, ArrowBack, Save, Search, ExpandMore, Refresh, TuneRounded, FileDownloadOutlined,
  Pause as SuspendIcon, PlayArrow as ReactivateIcon,
  Cancel as CancelIcon, Update as ExtendIcon,
  TrendingUp as UpgradeIcon,
  History as LedgerIcon, HistoryOutlined as AuditIcon, Close as CloseIcon,
  LocalOfferOutlined, CheckCircle,
  AccountCircleOutlined, CreditCard, AttachFileOutlined,
  DeleteOutline, InsertDriveFileOutlined, CheckCircleOutline, PendingOutlined,
} from '@mui/icons-material';
import { usePermissions } from '../../../../contexts/PermissionContext';
import * as api from '../../../../services/subscriptionAPI';

// ── constants (identical to PlansManagement) ──────────────────────────────────

const CARD_SX = {
  border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper',
};
const ACCORDION_SX = {
  ...CARD_SX, boxShadow: 'none', '&:before': { display: 'none' },
  '&.Mui-expanded': { borderColor: 'primary.light' },
};
const TH = {
  fontWeight: 700, fontSize: 11, color: 'text.secondary',
  letterSpacing: 0.6, textTransform: 'uppercase', bgcolor: 'grey.50', py: 1.5,
};
const _BTN      = { width: 30, height: 30, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' };
/* Reusable pagination bar — renders prev/next + numbered pages */
/* Sortable column header */
const SortTH = ({ label, field, sortBy, sortDir, onSort, sx = {} }) => (
  <Box display="flex" alignItems="center" gap={0.5} sx={{ cursor: 'pointer', userSelect: 'none', ...sx }}
    onClick={() => onSort(field)}>
    {label}
    <Box sx={{ display: 'flex', flexDirection: 'column', ml: 0.25, opacity: sortBy === field ? 1 : 0.3 }}>
      <svg width="8" height="5" viewBox="0 0 8 5"><path d="M4 0L8 5H0Z" fill={sortBy === field && sortDir === 'asc' ? 'currentColor' : '#9ca3af'}/></svg>
      <svg width="8" height="5" viewBox="0 0 8 5"><path d="M4 5L0 0H8Z" fill={sortBy === field && sortDir === 'desc' ? 'currentColor' : '#9ca3af'}/></svg>
    </Box>
  </Box>
);

const BTN_PRIMARY  = { ..._BTN, color: 'primary.main',   '&:hover': { bgcolor: 'primary.50',   borderColor: 'primary.200'   } };
const BTN_SUCCESS  = { ..._BTN, color: 'success.main',   '&:hover': { bgcolor: 'success.50',   borderColor: 'success.200'   } };
const BTN_ERROR    = { ..._BTN, color: 'error.main',     '&:hover': { bgcolor: 'error.50',     borderColor: 'error.200'     } };
const BTN_WARNING  = { ..._BTN, color: 'warning.dark',   '&:hover': { bgcolor: 'warning.50',   borderColor: 'warning.200'   } };
const BTN_SECONDARY= { ..._BTN, color: 'secondary.main', '&:hover': { bgcolor: 'secondary.50', borderColor: 'secondary.200' } };
const BTN_GREY     = { ..._BTN, color: 'text.secondary', '&:hover': { bgcolor: 'grey.100',     borderColor: 'grey.300'      } };

const STATUS_COLORS = {
  active:    { bg: 'success.50', color: 'success.dark', border: 'success.200' },
  suspended: { bg: 'warning.50', color: 'warning.dark', border: 'warning.200' },
  grace:     { bg: 'warning.50', color: 'warning.dark', border: 'warning.200' },
  expired:   { bg: 'grey.100',   color: 'text.secondary', border: 'grey.300' },
  cancelled: { bg: 'error.50',   color: 'error.dark',    border: 'error.200' },
};

const BILLING_LABELS = {
  monthly: 'Monthly', yearly: 'Yearly', one_time: 'One-time',
};

const fmt = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const calcExpiry = (startIso, billingPeriod) => {
  if (!startIso || !billingPeriod) return '';
  const d = new Date(startIso);
  switch (billingPeriod) {
    case 'monthly':  d.setMonth(d.getMonth() + 1);         break;
    case 'yearly':   d.setFullYear(d.getFullYear() + 1);   break;
    default:         d.setFullYear(d.getFullYear() + 1);   break;
  }
  return d.toISOString().slice(0, 10);
};

const graceDateStr = (expiryIso) => {
  if (!expiryIso) return '';
  const d = new Date(expiryIso);
  d.setDate(d.getDate() + 90);
  return fmt(d.toISOString());
};

const EMPTY_ASSIGN = {
  plan_version_id: '', billing_period: '',
  start_date: new Date().toISOString().slice(0, 10),
  expiry_date: '', duration_days: '', bundled_tokens: 0, admin_notes: '',
};

const EMPTY_PAY = {
  mode: '', status: 'captured', add_tokens_now: true,
  amount: '', currency_code: 'INR', payment_date: '',
  reference_number: '', cheque_no: '', utr_no: '',
  transaction_id: '', receipt_no: '', notes: '',
};

const PAY_MODES = [
  { value: 'credit_card',  label: 'Credit Card',  online: true  },
  { value: 'debit_card',   label: 'Debit Card',   online: true  },
  { value: 'netbanking',   label: 'Net Banking',  online: true  },
  { value: 'upi',          label: 'UPI',          online: true  },
  { value: 'cash',         label: 'Cash',         online: false },
  { value: 'cheque',       label: 'Cheque',       online: false },
];

const NUM_NO_SPIN_SUB = {
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
    { WebkitAppearance: 'none', margin: 0 },
  '& input[type=number]': { MozAppearance: 'textfield' },
};

// ── sub-components ─────────────────────────────────────────────────────────────

function StatusChip({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.expired;
  return (
    <Chip size="small" label={status}
      sx={{ fontWeight: 700, fontSize: 11, textTransform: 'capitalize',
        height: 22, bgcolor: c.bg, color: c.color,
        border: '1px solid', borderColor: c.border,
        '& .MuiChip-label': { px: 1 } }} />
  );
}

function SectionLabel({ label }) {
  return (
    <Typography variant="body1" fontWeight={700} letterSpacing={-0.2}>{label}</Typography>
  );
}

// ── main ───────────────────────────────────────────────────────────────────────

export default function SubscriptionsManagement() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canView       = hasPermission('Subscriptions', 'view');
  const canAssign     = hasPermission('Subscriptions', 'assign');
  const canSuspend    = hasPermission('Subscriptions', 'suspend');
  const canReactivate = hasPermission('Subscriptions', 'reactivate');
  const canCancel     = hasPermission('Subscriptions', 'cancel');
  const canExtend     = hasPermission('Subscriptions', 'extend');

  const [view, setView] = useState('list'); // 'list' | 'assign' | 'upgrade'

  // ── list state ─────────────────────────────────────────────────────────────
  const [items, setItems]           = useState([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(0);   // 0-indexed for TablePagination
  const [perPage, setPerPage]       = useState(10);
  const [sortBy, setSortBy]         = useState('created_at');
  const [sortDir, setSortDir]       = useState('desc');
  const [listLoading, setListLoading] = useState(false);
  const [search, setSearch]         = useState('');
  const [statusFilter, setStatus]   = useState('');

  // ── assign state ───────────────────────────────────────────────────────────
  const [form, setForm]                 = useState(EMPTY_ASSIGN);
  const [planVersions, setPlanVersions] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [saving, setSaving]             = useState(false);
  const [planDirection, setPlanDirection] = useState('upgrade'); // 'upgrade' | 'downgrade'

  // ── addon assign state ────────────────────────────────────────────────────
  const EMPTY_ADDON_FORM = { addon_id: '', currency_code: 'INR', admin_notes: '' };
  const [assignType, setAssignType]           = useState('plan'); // 'plan' | 'addon'
  const [availableAddons, setAvailableAddons] = useState([]);
  const [selectedAddon, setSelectedAddon]     = useState(null);
  const [addonForm, setAddonForm]             = useState(EMPTY_ADDON_FORM);

  // ── merchant lookup ────────────────────────────────────────────────────────
  const [merchantEmail, setMerchantEmail]   = useState('');
  const [merchantInfo, setMerchantInfo]     = useState(null);
  const [mlLoading, setMlLoading]           = useState(false);
  const [mlError, setMlError]               = useState('');

  // ── payment form ───────────────────────────────────────────────────────────
  const [payForm, setPayForm]   = useState(EMPTY_PAY);
  const setP = (k, v) => setPayForm(f => ({ ...f, [k]: v }));

  // ── file attachments ───────────────────────────────────────────────────────
  const [attachments, setAttachments] = useState([]);  // [{id, file, url, uploading, error}]

  // ── coupon state (assign dialog) ───────────────────────────────────────────
  const [couponInput, setCouponInput]       = useState('');
  const [couponPreview, setCouponPreview]   = useState(null);
  const [couponError, setCouponError]       = useState('');
  const [couponApplying, setCouponApplying] = useState(false);

  // ── upgrade view ───────────────────────────────────────────────────────────
  const [upgradeSub, setUpgradeSub]         = useState(null);
  const [upgradeForm, setUpgradeForm]       = useState({
    plan_version_id: '', billing_period: '', start_date: '', expiry_date: '',
    bundled_tokens: '', admin_notes: '',
  });
  const [upgradeSelectedPlan, setUpgradeSelectedPlan] = useState(null);
  const [upgradeCoupon, setUpgradeCoupon]             = useState('');
  const [upgradePayForm, setUpgradePayForm] = useState({
    mode: '', status: 'captured', add_tokens_now: true, amount: '', currency_code: 'INR',
    payment_date: '', reference_number: '', cheque_no: '', utr_no: '',
    transaction_id: '', receipt_no: '', notes: '',
  });
  const setUP = (k, v) => setUpgradePayForm(f => ({ ...f, [k]: v }));
  const [upgrading, setUpgrading]           = useState(false);

  // ── extend dialog ──────────────────────────────────────────────────────────
  const [extendSub, setExtendSub]   = useState(null);
  const [extendForm, setExtendForm] = useState({ expiry_date: '', reason: '' });
  const [extending, setExtending]   = useState(false);

  // ── inline validation errors ───────────────────────────────────────────────
  const [assignErrors, setAssignErrors] = useState({});
  const [extendErrors, setExtendErrors] = useState({});

  // ── cancel dialog ──────────────────────────────────────────────────────────
  const [cancelSub, setCancelSub]     = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling]   = useState(false);

  // ── ledger dialog ──────────────────────────────────────────────────────────
  const [ledgerSub, setLedgerSub]         = useState(null);
  const [ledger, setLedger]               = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const show = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  // ── load list ──────────────────────────────────────────────────────────────
  const loadItems = useCallback(async () => {
    setListLoading(true);
    try {
      const r = await api.getSubscriptions({
        search: search || undefined,
        status: statusFilter || undefined,
        page: page + 1,
        per_page: perPage,
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch { show('Failed to load subscriptions', 'error'); }
    finally { setListLoading(false); }
  }, [search, statusFilter, page, perPage, sortBy, sortDir]);

  // Reset to page 0 when filters or sort changes
  useEffect(() => { setPage(0); }, [search, statusFilter, sortBy, sortDir]);

  useEffect(() => { if (view === 'list') loadItems(); }, [view, loadItems]);

  // ── open assign page ───────────────────────────────────────────────────────
  // Debounced merchant search for the autocomplete suggestion list
  const [merchantSuggestions, setMerchantSuggestions] = useState([]);
  const [mlSearchLoading, setMlSearchLoading] = useState(false);

  useEffect(() => {
    if (view !== 'assign' || merchantEmail.length < 2) { setMerchantSuggestions([]); return; }
    const t = setTimeout(async () => {
      setMlSearchLoading(true);
      try {
        const r = await api.searchMerchants(merchantEmail);
        setMerchantSuggestions(r.data.items || []);
      } catch { setMerchantSuggestions([]); }
      finally { setMlSearchLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [merchantEmail, view]);

  // Reset plan selection when merchant changes
  useEffect(() => {
    setForm(f => ({ ...f, plan_version_id: '', billing_period: '', bundled_tokens: 0, expiry_date: '', duration_days: '' }));
    setSelectedPlan(null);
    setPlanDirection('upgrade');
  }, [merchantInfo]);

  // Called when user selects a suggestion — fetches full profile
  const selectMerchant = async (merchant) => {
    if (!merchant) { setMerchantInfo(null); return; }
    setMlLoading(true); setMlError('');
    try {
      const mr = await api.lookupMerchant({ user_id: merchant.user_id });
      setMerchantInfo(mr.data);
      // Clear addon selection whenever merchant changes
      setSelectedAddon(null);
      setAddonForm(f => ({ ...f, addon_id: '' }));
      // Load enriched addon list for this merchant (best-effort — falls back to generic list)
      try {
        const ar = await api.getAddonsForMerchant(merchant.user_id);
        setAvailableAddons((ar.data?.items || []).filter(a => a.status === 1));
      } catch {
        // Addon enrichment failed — leave the generic list from openAssign in place
      }
    } catch (e) {
      setMlError(e.response?.data?.message || 'Failed to load merchant details');
    } finally { setMlLoading(false); }
  };

  const filteredPlanVersions = useMemo(() => {
    if (!merchantInfo?.v2_subscription) return planVersions;
    const { plan_code, plan_tier: curTier = 0, status } = merchantInfo.v2_subscription;
    if (status === 'grace') {
      if (planDirection === 'renew')
        return planVersions.filter(p => p.plan_code === plan_code);
      if (planDirection === 'downgrade')
        return planVersions.filter(p => p.plan_code !== plan_code && (p.plan_tier ?? 0) < curTier);
      return planVersions.filter(p => p.plan_code !== plan_code && (p.plan_tier ?? 0) > curTier);
    }
    return planVersions.filter(p => p.plan_code !== plan_code && (p.plan_tier ?? 0) > curTier);
  }, [planVersions, merchantInfo, planDirection]);

  const uploadFiles = async (files) => {
    for (const file of Array.from(files)) {
      const uid = Date.now() + Math.random();
      setAttachments(a => [...a, { uid, file, url: null, uploading: true, error: null }]);
      try {
        const r = await api.uploadPaymentAttachment(file);
        setAttachments(a => a.map(x => x.uid === uid ? { ...x, url: r.data.url, uploading: false } : x));
      } catch {
        setAttachments(a => a.map(x => x.uid === uid ? { ...x, uploading: false, error: 'Upload failed' } : x));
      }
    }
  };

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    if (!merchantInfo)         { setCouponError('Look up a merchant first');      return; }
    if (!form.plan_version_id) { setCouponError('Select a plan first');           return; }
    if (!form.billing_period)  { setCouponError('Select a billing period first'); return; }
    setCouponApplying(true);
    setCouponError('');
    try {
      const r = await api.previewCoupon({
        code,
        user_id:         merchantInfo.user_id,
        plan_version_id: Number(form.plan_version_id),
        billing_period:  form.billing_period,
      });
      setCouponPreview(r.data);
      setCouponError('');
    } catch (e) {
      setCouponError(e.response?.data?.message || 'Invalid coupon');
      setCouponPreview(null);
    } finally { setCouponApplying(false); }
  };

  const removeCoupon = () => {
    setCouponInput('');
    setCouponPreview(null);
    setCouponError('');
  };

  const handleExport = async () => {
    try {
      const res = await api.exportSubscriptions({
        status: statusFilter || undefined,
        search: search || undefined,
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `subscriptions_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { show('Export failed', 'error'); }
  };

  const openAssign = async () => {
    setForm(EMPTY_ASSIGN);
    setSelectedPlan(null);
    setAssignType('plan');
    setSelectedAddon(null);
    setAddonForm(EMPTY_ADDON_FORM);
    setMerchantEmail('');
    setMerchantInfo(null);
    setMlError('');
    setMerchantSuggestions([]);
    setPayForm(EMPTY_PAY);
    setAttachments([]);
    removeCoupon();
    try {
      const [r1, r2] = await Promise.all([
        api.getSubscriptionPlanVersions(),
        api.getAddonsForAssign(),
      ]);
      setPlanVersions(r1.data || []);
      setAvailableAddons((r2.data?.items || []).filter(a => a.status === 1));
    } catch { show('Failed to load plans / add-ons', 'error'); }
    setView('assign');
  };


  // ── plan selection: auto-fill period / tokens / expiry ────────────────────
  const calcDurationDays = (startIso, expiryIso) => {
    if (!startIso || !expiryIso) return '';
    const diff = Math.round((new Date(expiryIso) - new Date(startIso)) / 86400000);
    return diff > 0 ? String(diff) : '';
  };

  const handlePlanSelect = (pvId) => {
    const pv = planVersions.find(p => p.plan_version_id === Number(pvId));
    setSelectedPlan(pv || null);
    const preferredCurrency = merchantInfo?.country_name === 'India' ? 'INR' : 'USD';
    const first  = pv?.pricing?.find(p => p.currency_code === preferredCurrency) || pv?.pricing?.[0];
    const period = first?.billing_period || '';
    const tokens = first?.bundled_tokens  || 0;
    const expiry = calcExpiry(form.start_date, period);
    const days   = calcDurationDays(form.start_date, expiry);
    setForm(f => ({ ...f, plan_version_id: pvId, billing_period: period, bundled_tokens: tokens, expiry_date: expiry, duration_days: days }));
    setP('currency_code', preferredCurrency);
    syncPayAmount(pv, period);
  };

  const handlePeriodChange = (period) => {
    const pricing = selectedPlan?.pricing?.find(p => p.billing_period === period);
    const tokens  = pricing?.bundled_tokens ?? form.bundled_tokens;
    const expiry  = calcExpiry(form.start_date, period);
    const days    = calcDurationDays(form.start_date, expiry);
    setForm(f => ({ ...f, billing_period: period, bundled_tokens: tokens, expiry_date: expiry, duration_days: days }));
    syncPayAmount(selectedPlan, period);
  };

  const handleStartChange = (val) => {
    const expiry = form.duration_days
      ? (() => { const d = new Date(val); d.setDate(d.getDate() + Number(form.duration_days)); return d.toISOString().slice(0, 10); })()
      : calcExpiry(val, form.billing_period);
    const days = calcDurationDays(val, expiry);
    setForm(f => ({ ...f, start_date: val, expiry_date: expiry, duration_days: days }));
  };

  const handleDurationChange = (val) => {
    const days = val.replace(/\D/g, '');
    if (!days) { setForm(f => ({ ...f, duration_days: '' })); return; }
    const d = new Date(form.start_date || new Date());
    d.setDate(d.getDate() + Number(days));
    const expiry = d.toISOString().slice(0, 10);
    setForm(f => ({ ...f, duration_days: days, expiry_date: expiry }));
  };

  const handleExpiryChange = (val) => {
    const days = calcDurationDays(form.start_date, val);
    setForm(f => ({ ...f, expiry_date: val, duration_days: days }));
    if (assignErrors.expiry_date) setAssignErrors(p => ({ ...p, expiry_date: '' }));
  };

  // ── plan selection helpers (auto-fill payment amount) ─────────────────────
  const syncPayAmount = (pv, period) => {
    if (!pv || !period) return;
    const pricing = pv.pricing?.find(p => p.billing_period === period);
    if (pricing?.mrp_incl_tax) setP('amount', String(pricing.mrp_incl_tax));
  };

  // ── submit assign ──────────────────────────────────────────────────────────
  const submitAssign = async () => {
    const errs = {};
    if (!form.plan_version_id) errs.plan_version_id = 'Select a plan';
    const merchantCurrency = merchantInfo?.country_name === 'India' ? 'INR' : 'USD';
    const availablePeriods = (selectedPlan?.pricing || []).filter(p => p.currency_code === merchantCurrency).map(p => p.billing_period);
    if (!form.billing_period && availablePeriods.length > 0) errs.billing_period = 'Select a billing period';
    if (!form.expiry_date)     errs.expiry_date     = 'Expiry date is required';
    if (payForm.mode && !payForm.amount) errs.pay_amount = 'Payment amount is required';
    if (Object.keys(errs).length > 0) { setAssignErrors(errs); return; }
    if (!merchantInfo) { show('Look up a merchant first', 'error'); return; }
    setAssignErrors({});
    setSaving(true);
    try {
      const payload = {
        user_id:         merchantInfo.user_id,
        plan_version_id: Number(form.plan_version_id),
        billing_period:  form.billing_period || undefined,
        start_date:      form.start_date,
        expiry_date:     form.expiry_date,
        bundled_tokens:  Number(form.bundled_tokens),
        admin_notes:     form.admin_notes || undefined,
        coupon_code:     couponPreview?.code || undefined,
      };

      if (payForm.mode) {
        const completedUrls = attachments.filter(a => a.url).map(a => a.url);
        payload.payment = {
          payment_mode:     payForm.mode,
          status:           payForm.status,
          add_tokens_now:   payForm.status === 'pending' ? payForm.add_tokens_now : true,
          amount:           Number(payForm.amount),
          currency_code:    payForm.currency_code || 'INR',
          payment_date:     payForm.payment_date || undefined,
          reference_number: payForm.reference_number || undefined,
          cheque_no:        payForm.cheque_no || undefined,
          utr_no:           payForm.utr_no || undefined,
          transaction_id:   payForm.transaction_id || undefined,
          receipt_no:       payForm.receipt_no || undefined,
          notes:            payForm.notes || undefined,
          attachment_urls:  completedUrls,
          gateway:          ['credit_card','debit_card','netbanking','upi'].includes(payForm.mode)
                              ? 'manual' : 'offline',
        };
      }

      if (merchantInfo.v2_subscription) {
        const upgradePayload = {
          plan_version_id: Number(form.plan_version_id),
          billing_period:  form.billing_period || undefined,
          start_date:      form.start_date || undefined,
          expiry_date:     form.expiry_date,
          bundled_tokens:  Number(form.bundled_tokens),
          admin_notes:     form.admin_notes || undefined,
          coupon_code:     couponPreview?.code || undefined,
          ...(payload.payment ? { payment: payload.payment } : {}),
        };
        await api.upgradeSubscription(merchantInfo.v2_subscription.id, upgradePayload);
        const direction = planDirection === 'renew' ? 'renewed' : planDirection === 'downgrade' ? 'downgraded' : 'upgraded';
        show(`Subscription ${direction} successfully`);
      } else {
        await api.assignSubscription(payload);
        show('Subscription assigned' + (payForm.mode ? ' with payment' : ''));
      }
      loadItems();
      setView('list');
    } catch (e) { show(e.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  // ── addon assign handlers ──────────────────────────────────────────────────
  const handleAddonSelect = (addonId) => {
    const addon = availableAddons.find(a => a.id === Number(addonId));
    setSelectedAddon(addon || null);
    const merchantCurr = merchantInfo?.country_name === 'India' ? 'INR' : 'USD';
    setAddonForm(f => ({ ...f, addon_id: addonId, currency_code: merchantCurr }));
    setP('currency_code', merchantCurr);
    if (addon) {
      const pricing = addon.pricing?.find(p => p.currency_code === merchantCurr && p.status === 1);
      if (pricing?.mrp_incl_tax) setP('amount', String(pricing.mrp_incl_tax));
    }
  };

  const submitAssignAddon = async () => {
    if (!merchantInfo) { show('Look up a merchant first', 'error'); return; }
    if (!addonForm.addon_id) { show('Select an add-on', 'error'); return; }
    if (payForm.mode && !payForm.amount) { show('Payment amount is required', 'error'); return; }
    setSaving(true);
    try {
      const payload = {
        user_id:       merchantInfo.user_id,
        addon_id:      Number(addonForm.addon_id),
        currency_code: addonForm.currency_code || 'INR',
        admin_notes:   addonForm.admin_notes || undefined,
      };
      if (payForm.mode) {
        const completedUrls = attachments.filter(a => a.url).map(a => a.url);
        payload.payment = {
          payment_mode:     payForm.mode,
          status:           payForm.status,
          add_tokens_now:   payForm.status === 'pending' ? payForm.add_tokens_now : true,
          amount:           Number(payForm.amount),
          currency_code:    payForm.currency_code || 'INR',
          payment_date:     payForm.payment_date || undefined,
          reference_number: payForm.reference_number || undefined,
          cheque_no:        payForm.cheque_no || undefined,
          utr_no:           payForm.utr_no || undefined,
          transaction_id:   payForm.transaction_id || undefined,
          receipt_no:       payForm.receipt_no || undefined,
          notes:            payForm.notes || undefined,
          attachment_urls:  completedUrls,
          gateway:          ['credit_card','debit_card','netbanking','upi'].includes(payForm.mode) ? 'manual' : 'offline',
        };
      }
      await api.assignAddonSubscription(payload);
      show('Add-on assigned' + (payForm.mode ? ' with payment' : ''));
      loadItems();
      setView('list');
    } catch (e) { show(e.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  // ── inline actions ─────────────────────────────────────────────────────────
  const toggleSuspend = async (sub) => {
    try {
      if (sub.status === 'active') { await api.suspendSubscription(sub.id); show('Suspended'); }
      else                         { await api.reactivateSubscription(sub.id); show('Reactivated'); }
      loadItems();
    } catch (e) { show(e.response?.data?.message || 'Action failed', 'error'); }
  };

  const submitExtend = async () => {
    if (!extendForm.expiry_date) { setExtendErrors({ expiry_date: 'New expiry date is required' }); return; }
    setExtendErrors({});
    setExtending(true);
    try {
      await api.extendSubscription(extendSub.id, extendForm);
      show('Extended'); setExtendSub(null); loadItems();
    } catch (e) { show(e.response?.data?.message || 'Extend failed', 'error'); }
    finally { setExtending(false); }
  };

  const submitCancel = async () => {
    setCancelling(true);
    try {
      await api.cancelSubscription(cancelSub.id, { reason: cancelReason });
      show('Cancelled'); setCancelSub(null); loadItems();
    } catch (e) { show(e.response?.data?.message || 'Cancel failed', 'error'); }
    finally { setCancelling(false); }
  };

  const openUpgrade = async (sub) => {
    setUpgradeSub(sub);
    setUpgradeSelectedPlan(null);
    setUpgradeCoupon('');
    const today = new Date().toISOString().slice(0, 10);
    setUpgradeForm({ plan_version_id: '', billing_period: '', start_date: today, expiry_date: '', bundled_tokens: '', admin_notes: '' });
    setUpgradePayForm({ mode: '', status: 'captured', add_tokens_now: true, amount: '', currency_code: sub?.merchant_country_name === 'India' ? 'INR' : ((sub?.merchant_mobile_code || '').trim() === '+91' ? 'INR' : (sub?.currency_code || 'INR')), payment_date: today + 'T00:00', reference_number: '', cheque_no: '', utr_no: '', transaction_id: '', receipt_no: '', notes: '' });
    try {
      const r = await api.getSubscriptionPlanVersions();
      setPlanVersions(r.data || []);
      setView('upgrade');
    } catch {
      show('Failed to load plans', 'error');
      setUpgradeSub(null);
    }
  };

  const submitUpgrade = async () => {
    if (!upgradeForm.plan_version_id) { show('Select a plan', 'error'); return; }
    if (!upgradeForm.expiry_date)     { show('Set expiry date', 'error'); return; }
    const _upCurr = upgradeSub?.merchant_country_name === 'India' ? 'INR' : ((upgradeSub?.merchant_mobile_code || '').trim() === '+91' ? 'INR' : (upgradeSub?.currency_code || 'INR'));
    const _upgradePeriods = (upgradeSelectedPlan?.pricing || []).filter(p => p.currency_code === _upCurr).map(p => p.billing_period);
    if (!upgradeForm.billing_period && _upgradePeriods.length > 0) { show('Select a billing period', 'error'); return; }
    setUpgrading(true);
    try {
      const payload = {
        plan_version_id: Number(upgradeForm.plan_version_id),
        billing_period:  upgradeForm.billing_period || undefined,
        start_date:      upgradeForm.start_date || undefined,
        expiry_date:     upgradeForm.expiry_date,
        bundled_tokens:  Number(upgradeForm.bundled_tokens) || 0,
        admin_notes:     upgradeForm.admin_notes || undefined,
      };
      if (upgradeCoupon) payload.coupon_code = upgradeCoupon;
      if (upgradePayForm.mode && upgradePayForm.amount) {
        payload.payment = {
          payment_mode:     upgradePayForm.mode,
          status:           upgradePayForm.status,
          add_tokens_now:   upgradePayForm.status === 'pending' ? upgradePayForm.add_tokens_now : true,
          amount:           Number(upgradePayForm.amount),
          currency_code:    upgradePayForm.currency_code,
          payment_date:     upgradePayForm.payment_date || undefined,
          reference_number: upgradePayForm.reference_number || undefined,
          cheque_no:        upgradePayForm.cheque_no || undefined,
          utr_no:           upgradePayForm.utr_no || undefined,
          transaction_id:   upgradePayForm.transaction_id || undefined,
          receipt_no:       upgradePayForm.receipt_no || undefined,
          notes:            upgradePayForm.notes || undefined,
          gateway: ['credit_card','debit_card','netbanking','upi'].includes(upgradePayForm.mode) ? 'manual' : 'offline',
        };
      }
      const r = await api.upgradeSubscription(upgradeSub.id, payload);
      const cf = r.data.carry_forward_tokens;
      show(`Upgraded${cf ? ` — ${cf} tokens carried forward` : ''}`);
      setUpgradeSub(null);
      setView('list');
      loadItems();
    } catch (e) { show(e.response?.data?.message || 'Upgrade failed', 'error'); }
    finally { setUpgrading(false); }
  };

  const openLedger = async (sub) => {
    setLedgerSub(sub); setLedger([]); setLedgerLoading(true);
    try { const r = await api.getSubscriptionLedger(sub.id); setLedger(r.data || []); }
    catch { show('Failed to load ledger', 'error'); }
    finally { setLedgerLoading(false); }
  };

  // ── guard ──────────────────────────────────────────────────────────────────
  if (!canView) return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography color="text.secondary">You don't have permission to view subscriptions.</Typography>
    </Box>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // ASSIGN VIEW
  // ══════════════════════════════════════════════════════════════════════════
  if (view === 'assign') {
    const merchantCurrency  = merchantInfo?.country_name === 'India' ? 'INR' : 'USD';
    const availablePeriods  = (selectedPlan?.pricing || []).filter(p => p.currency_code === merchantCurrency).map(p => p.billing_period);
    const selectedPricing   = selectedPlan?.pricing?.find(p => p.billing_period === form.billing_period && p.currency_code === merchantCurrency);
    const isOnlineMode      = ['credit_card','debit_card','netbanking','upi'].includes(payForm.mode);
    const isPending         = payForm.status === 'pending';

    return (
      <Box sx={{ p: 3 }}>

        {/* ── Sticky header ──────────────────────────────────────────────── */}
        <Box display="flex" justifyContent="space-between" alignItems="center"
          sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: 'background.paper',
            borderBottom: '1px solid', borderColor: 'divider',
            py: 1.5, mb: 3, mx: -3, px: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Tooltip title="Back to list">
              <IconButton onClick={() => setView('list')} size="small"
                sx={{ border: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'grey.100' } }}>
                <ArrowBack fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2} letterSpacing={-0.3}>
                Assign Subscription
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {merchantInfo
                  ? `${merchantInfo.display_name} · ${merchantInfo.email}`
                  : 'Look up a merchant to begin'}
              </Typography>
            </Box>
          </Box>
          <Button variant="contained"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save />}
            onClick={assignType === 'addon' ? submitAssignAddon : submitAssign}
            disabled={saving || !merchantInfo || (assignType === 'plan' ? !form.plan_version_id : !addonForm.addon_id)}
            sx={{ borderRadius: 2, fontWeight: 700, minWidth: 150,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
            {saving ? 'Saving…'
              : assignType === 'addon'
                ? (payForm.mode ? 'Assign Add-on & Record Payment' : 'Assign Add-on')
              : (merchantInfo?.v2_subscription?.status === 'grace' && planDirection === 'downgrade')
                ? (payForm.mode ? 'Downgrade & Record Payment' : 'Downgrade')
              : merchantInfo?.v2_subscription
                ? (payForm.mode ? 'Upgrade & Record Payment' : 'Upgrade')
              : (payForm.mode ? 'Assign & Record Payment' : 'Assign')}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* ── 1. Merchant Information ───────────────────────────────────── */}
          <Accordion defaultExpanded disableGutters elevation={0} sx={ACCORDION_SX}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <SectionLabel label="Merchant Information" />
                {merchantInfo && (
                  <Chip size="small" label={merchantInfo.display_name} color="primary"
                    sx={{ fontWeight: 700, fontSize: 11, height: 20 }} />
                )}
              </Box>
            </AccordionSummary>
            <Divider />
            <AccordionDetails sx={{ p: 3 }}>
              {/* Live search autocomplete */}
              <Box mb={merchantInfo ? 2.5 : 0}>
                <Autocomplete
                  freeSolo
                  options={merchantSuggestions}
                  getOptionLabel={o => typeof o === 'string' ? o : `${o.display_name} — ${o.email}`}
                  filterOptions={x => x}
                  loading={mlSearchLoading}
                  inputValue={merchantEmail}
                  onInputChange={(_, v, reason) => {
                    setMerchantEmail(v);
                    if (reason === 'clear' || v === '') { setMerchantInfo(null); setMlError(''); }
                  }}
                  onChange={(_, v) => { if (v && typeof v === 'object') selectMerchant(v); }}
                  renderOption={(props, o) => {
                    const hasSub = !!o.active_plan_name;
                    return (
                      <Box component="li" {...props} key={o.user_id}
                        sx={{ cursor: 'pointer' }}>
                        <Box width="100%">
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" fontWeight={700} lineHeight={1.2}>
                              {o.display_name}
                            </Typography>
                            {hasSub && (
                              <Chip size="small" color="warning"
                                label={`Active: ${o.active_plan_name}`}
                                sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {o.email}{o.mobile ? ` · ${o.mobile}` : ''}{o.whatsapp ? ` · WhatsApp: ${o.whatsapp}` : ''}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }}
                  renderInput={params => (
                    <TextField {...params} size="small" label="Search Merchant *"
                      placeholder="Type name, email, or WhatsApp number…"
                      error={!!mlError} helperText={
                        mlError
                          ? mlError
                          : merchantInfo?.v2_subscription
                            ? <Chip size="small" color="warning"
                                label={`Active plan: ${merchantInfo.v2_subscription.plan_name}`}
                                sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />
                            : 'Type 2+ characters to search by name, email, or WhatsApp'
                      }
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              {mlLoading
                                ? <CircularProgress size={14} />
                                : <AccountCircleOutlined sx={{ fontSize: 18, color: 'text.disabled' }} />}
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }} />
                  )}
                  noOptionsText={merchantEmail.length < 2 ? 'Type 2+ characters to search…' : 'No merchants found'}
                />
              </Box>

              {/* Merchant profile card */}
              {merchantInfo && (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  {/* Left: basic info */}
                  <Box sx={{ bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider',
                    borderRadius: 2, p: 2 }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary"
                      textTransform="uppercase" letterSpacing={0.5} display="block" mb={1.5}>
                      Contact Details
                    </Typography>
                    {[
                      ['Name',     merchantInfo.display_name],
                      ['Email',    merchantInfo.email],
                      ['Mobile',   [merchantInfo.mobile_code, merchantInfo.mobile].filter(Boolean).join(' ') || '—'],
                      ['WhatsApp', [merchantInfo.whatsapp_code, merchantInfo.whatsapp].filter(Boolean).join(' ') || '—'],
                      ['Country',  merchantInfo.country_name || '—'],
                      ['GST',      merchantInfo.gst_number || '—'],
                    ].map(([k, v]) => (
                      <Box key={k} display="flex" gap={1} mb={0.75}>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 72 }}>{k}</Typography>
                        <Typography variant="caption" fontWeight={600}>{v}</Typography>
                      </Box>
                    ))}
                  </Box>
                  {/* Right: existing v2 subscription */}
                  <Box sx={{ bgcolor: merchantInfo.v2_subscription ? 'warning.50' : 'success.50',
                    border: '1px solid',
                    borderColor: merchantInfo.v2_subscription ? 'warning.200' : 'success.200',
                    borderRadius: 2, p: 2 }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary"
                      textTransform="uppercase" letterSpacing={0.5} display="block" mb={1.5}>
                      Current v2 Subscription
                    </Typography>
                    {merchantInfo.v2_subscription ? (
                      <>
                        <Chip size="small" label="Active subscription"
                          color="warning" sx={{ fontWeight: 700, fontSize: 10, height: 18, mb: 1 }} />
                        {[
                          ['Plan',    merchantInfo.v2_subscription.plan_name],
                          ['Status',  merchantInfo.v2_subscription.status],
                          ['Expires', merchantInfo.v2_subscription.expiry_date
                            ? new Date(merchantInfo.v2_subscription.expiry_date)
                                .toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })
                            : '—'],
                          ['Balance', `${merchantInfo.v2_subscription.token_balance} tokens`],
                        ].map(([k, v]) => (
                          <Box key={k} display="flex" gap={1} mb={0.75}>
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>{k}</Typography>
                            <Typography variant="caption" fontWeight={700}>{v}</Typography>
                          </Box>
                        ))}
                      </>
                    ) : (
                      <Box display="flex" alignItems="center" gap={1} mt={1}>
                        <CheckCircleOutline sx={{ color: 'success.main', fontSize: 18 }} />
                        <Typography variant="body2" color="success.dark" fontWeight={600}>
                          No active subscription — safe to assign
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>

          {/* ── Assign type toggle ────────────────────────────────────────── */}
          <Box sx={{ display: 'flex', gap: 1, px: 0 }}>
            <Button
              variant={assignType === 'plan' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => { setAssignType('plan'); setSelectedAddon(null); setAddonForm(EMPTY_ADDON_FORM); setP('amount', ''); }}
              sx={{ borderRadius: 2, fontWeight: 700 }}>
              Plan
            </Button>
            <Button
              variant={assignType === 'addon' ? 'contained' : 'outlined'}
              color={assignType === 'addon' ? 'secondary' : 'inherit'}
              size="small"
              onClick={() => { setAssignType('addon'); setSelectedPlan(null); setForm(EMPTY_ASSIGN); setP('amount', ''); removeCoupon(); }}
              sx={{ borderRadius: 2, fontWeight: 700 }}>
              Add-on
            </Button>
          </Box>

          {/* ── 2. Plan & Dates ───────────────────────────────────────────── */}
          {assignType === 'plan' && <Accordion defaultExpanded disableGutters elevation={0} sx={ACCORDION_SX}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <SectionLabel label={
                  merchantInfo?.v2_subscription?.status === 'grace'
                    ? (planDirection === 'downgrade' ? 'Downgrade Plan' : planDirection === 'renew' ? 'Renew Plan' : 'Upgrade Plan')
                    : merchantInfo?.v2_subscription ? 'Upgrade Plan'
                    : 'Plan & Subscription Dates'
                } />
                {form.plan_version_id && selectedPlan && (
                  <Chip size="small" label={selectedPlan.display_name} color="primary"
                    sx={{ fontWeight: 700, fontSize: 11, height: 20 }} />
                )}
              </Box>
            </AccordionSummary>
            <Divider />
            <AccordionDetails sx={{ p: 3 }}>
              {merchantInfo?.v2_subscription?.status === 'grace' && (
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    size="small"
                    variant={planDirection === 'upgrade' ? 'contained' : 'outlined'}
                    onClick={() => {
                      setPlanDirection('upgrade');
                      setForm(f => ({ ...f, plan_version_id: '', billing_period: '', expiry_date: '', bundled_tokens: 0 }));
                      setSelectedPlan(null);
                      setP('amount', '');
                      removeCoupon();
                    }}
                    sx={{ borderRadius: 2, fontWeight: 700 }}>
                    Upgrade
                  </Button>
                  <Button
                    size="small"
                    variant={planDirection === 'downgrade' ? 'contained' : 'outlined'}
                    color={planDirection === 'downgrade' ? 'warning' : 'inherit'}
                    onClick={() => {
                      setPlanDirection('downgrade');
                      setForm(f => ({ ...f, plan_version_id: '', billing_period: '', expiry_date: '', bundled_tokens: 0 }));
                      setSelectedPlan(null);
                      setP('amount', '');
                      removeCoupon();
                    }}
                    sx={{ borderRadius: 2, fontWeight: 700 }}>
                    Downgrade
                  </Button>
                  <Button
                    size="small"
                    variant={planDirection === 'renew' ? 'contained' : 'outlined'}
                    color={planDirection === 'renew' ? 'success' : 'inherit'}
                    onClick={() => {
                      setPlanDirection('renew');
                      setForm(f => ({ ...f, plan_version_id: '', billing_period: '', expiry_date: '', bundled_tokens: 0 }));
                      setSelectedPlan(null);
                      setP('amount', '');
                      removeCoupon();
                    }}
                    sx={{ borderRadius: 2, fontWeight: 700 }}>
                    Renew
                  </Button>
                </Box>
              )}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2.5 }}>
                <TextField select label="Plan *" size="small" fullWidth
                  value={form.plan_version_id}
                  error={!!assignErrors.plan_version_id}
                  helperText={assignErrors.plan_version_id || ' '}
                  onChange={e => { handlePlanSelect(e.target.value); if (assignErrors.plan_version_id) setAssignErrors(p => ({ ...p, plan_version_id: '' })); }}>
                  <MenuItem value="">— Select plan —</MenuItem>
                  {filteredPlanVersions.length === 0 && merchantInfo?.v2_subscription && (
                    <MenuItem disabled value="__none__">No eligible plans available</MenuItem>
                  )}
                  {filteredPlanVersions.map(pv => (
                    <MenuItem key={pv.plan_version_id} value={pv.plan_version_id}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight={600}>{pv.display_name}</Typography>
                        <Typography variant="caption" color="text.disabled">v{pv.version_number}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>

                {availablePeriods.length > 0 ? (
                  <FormControl size="small" fullWidth error={!!assignErrors.billing_period}>
                    <InputLabel>Billing Period *</InputLabel>
                    <Select label="Billing Period *" value={form.billing_period}
                      onChange={e => { handlePeriodChange(e.target.value); if (assignErrors.billing_period) setAssignErrors(p => ({ ...p, billing_period: '' })); }}>
                      {availablePeriods.map(p => {
                        const pr = selectedPlan?.pricing?.find(x => x.billing_period === p && x.currency_code === merchantCurrency);
                        return (
                          <MenuItem key={p} value={p}>
                            {BILLING_LABELS[p] || p}{pr ? ` — ${pr.currency_code} ${Number(pr.mrp_excl_tax).toLocaleString('en-IN')} excl. GST` : ''}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {assignErrors.billing_period && (
                      <Typography fontSize={11} color="error" mt={0.5} ml={1.5}>{assignErrors.billing_period}</Typography>
                    )}
                  </FormControl>
                ) : <Box />}
              </Box>

              {/* Plan features preview */}
              {selectedPricing && (
                <Box sx={{ bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200',
                  borderRadius: 2, px: 2, py: 1.5, mb: 2.5, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Price (incl. tax)</Typography>
                    <Typography fontWeight={800} fontSize={16}>
                      {selectedPricing.currency_code} {Number(selectedPricing.mrp_incl_tax || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Bundled Tokens</Typography>
                    <Typography fontWeight={800} fontSize={16}>{selectedPricing.bundled_tokens}</Typography>
                  </Box>
                  {form.billing_period && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Period</Typography>
                      <Typography fontWeight={700} fontSize={14}>{BILLING_LABELS[form.billing_period] || form.billing_period}</Typography>
                    </Box>
                  )}
                </Box>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 2, mb: 1.5 }}>
                <TextField label="Start Date" type="date" size="small" fullWidth
                  value={form.start_date}
                  onChange={e => handleStartChange(e.target.value)}
                  InputLabelProps={{ shrink: true }} />
                <TextField label="Duration (days)" type="number" size="small" fullWidth
                  value={form.duration_days}
                  onChange={e => handleDurationChange(e.target.value)}
                  inputProps={{ min: 1 }}
                  helperText="Sets expiry automatically"
                  sx={NUM_NO_SPIN_SUB} />
                <TextField label="Expiry Date *" type="date" size="small" fullWidth
                  value={form.expiry_date}
                  error={!!assignErrors.expiry_date}
                  helperText={assignErrors.expiry_date || (form.billing_period ? `Auto-filled from start + ${BILLING_LABELS[form.billing_period] || form.billing_period}` : ' ')}
                  onChange={e => handleExpiryChange(e.target.value)}
                  InputLabelProps={{ shrink: true }} />
                <TextField label="Bundled Tokens" type="number" size="small" fullWidth
                  value={form.bundled_tokens}
                  onChange={e => setForm(f => ({ ...f, bundled_tokens: e.target.value }))}
                  inputProps={{ min: 0 }}
                  helperText="Auto-filled · adjust if needed"
                  sx={NUM_NO_SPIN_SUB} />
              </Box>

              {form.expiry_date && (
                <Box sx={{ px: 1.5, py: 1, bgcolor: 'info.50', borderRadius: 1.5,
                  border: '1px solid', borderColor: 'info.200' }}>
                  <Typography variant="caption" color="info.dark">
                    Token grace period extends 90 days past expiry → tokens valid until{' '}
                    <strong>{graceDateStr(form.expiry_date)}</strong>
                  </Typography>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>}

          {/* ── 3. Add-on ─────────────────────────────────────────────────── */}
          {assignType === 'addon' && (
            <Accordion defaultExpanded disableGutters elevation={0} sx={ACCORDION_SX}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <SectionLabel label="Add-on" />
                  {selectedAddon && (
                    <Chip size="small" label={selectedAddon.display_name} color="secondary"
                      sx={{ fontWeight: 700, fontSize: 11, height: 20 }} />
                  )}
                </Box>
              </AccordionSummary>
              <Divider />
              <AccordionDetails sx={{ p: 3 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                  <TextField select label="Add-on *" size="small" fullWidth
                    value={addonForm.addon_id}
                    onChange={e => handleAddonSelect(e.target.value)}>
                    <MenuItem value="">— Select add-on —</MenuItem>
                    {availableAddons.map(a => {
                      const locked = !!a.lock_reason;
                      const lockLabel = a.lock_reason === 'included_in_plan'
                        ? 'included in plan'
                        : a.lock_reason === 'already_owned'
                          ? 'already active'
                          : a.lock_reason === 'upgrade_required'
                            ? 'upgrade required'
                            : null;
                      return (
                        <MenuItem key={a.id} value={a.id} disabled={locked}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" fontWeight={600}
                              color={locked ? 'text.disabled' : 'text.primary'}>
                              {a.display_name}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              {(a.addon_category || '').replace(/_/g, ' ')}
                            </Typography>
                            {lockLabel && (
                              <Typography variant="caption"
                                color={a.lock_reason === 'included_in_plan' ? 'success.main' : 'warning.main'}
                                fontWeight={700}>
                                {lockLabel}
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </TextField>
                  <TextField select label="Currency" size="small" fullWidth
                    value={addonForm.currency_code}
                    onChange={e => {
                      const curr = e.target.value;
                      setAddonForm(f => ({ ...f, currency_code: curr }));
                      setP('currency_code', curr);
                      if (selectedAddon) {
                        const pr = selectedAddon.pricing?.find(p => p.currency_code === curr && p.status === 1);
                        if (pr?.mrp_incl_tax) setP('amount', String(pr.mrp_incl_tax));
                      }
                    }}>
                    <MenuItem value="INR">INR</MenuItem>
                    <MenuItem value="USD">USD</MenuItem>
                  </TextField>
                </Box>

                {selectedAddon && (() => {
                  const pr = selectedAddon.pricing?.find(p => p.currency_code === addonForm.currency_code && p.status === 1);
                  return pr ? (
                    <Box sx={{ bgcolor: 'secondary.50', border: '1px solid', borderColor: 'secondary.200',
                      borderRadius: 2, px: 2, py: 1.5, mb: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Price (incl. tax)</Typography>
                        <Typography fontWeight={800} fontSize={16}>
                          {pr.currency_code} {Number(pr.mrp_incl_tax || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                      {pr.tokens_grant > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Tokens Granted</Typography>
                          <Typography fontWeight={800} fontSize={16}>{pr.tokens_grant}</Typography>
                        </Box>
                      )}
                      <Box>
                        <Typography variant="caption" color="text.secondary">Category</Typography>
                        <Typography fontWeight={700} fontSize={14}>
                          {(selectedAddon.addon_category || '').replace(/_/g, ' ')}
                        </Typography>
                      </Box>
                    </Box>
                  ) : selectedAddon ? (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="warning.main">
                        No pricing found for {addonForm.currency_code} — add-on can still be assigned without payment.
                      </Typography>
                    </Box>
                  ) : null;
                })()}

                <TextField label="Admin Notes" size="small" fullWidth multiline rows={2}
                  value={addonForm.admin_notes}
                  onChange={e => setAddonForm(f => ({ ...f, admin_notes: e.target.value }))}
                  placeholder="Optional internal note" />
              </AccordionDetails>
            </Accordion>
          )}

          {/* ── 3b. Coupon (plan only) ────────────────────────────────────── */}
          {assignType === 'plan' && <Accordion disableGutters elevation={0} sx={ACCORDION_SX}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <SectionLabel label="Coupon" />
                {couponPreview && (
                  <Chip size="small" icon={<LocalOfferOutlined sx={{ fontSize: 13 }} />}
                    label={couponPreview.code} color="success"
                    sx={{ fontWeight: 700, fontSize: 11, height: 20, '& .MuiChip-label': { px: 1 } }} />
                )}
              </Box>
            </AccordionSummary>
            <Divider />
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Optionally apply a discount coupon to this subscription.
              </Typography>
              {couponPreview ? (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                  bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200',
                  borderRadius: 2, px: 2.5, py: 2 }}>
                  <Box display="flex" alignItems="flex-start" gap={1.5}>
                    <CheckCircle sx={{ color: 'success.main', mt: 0.2, fontSize: 20 }} />
                    <Box>
                      <Typography fontWeight={700} fontSize={14} color="success.dark">
                        {couponPreview.code}
                        {couponPreview.description && (
                          <Typography component="span" fontWeight={400} fontSize={12}
                            color="text.secondary" ml={1}>— {couponPreview.description}</Typography>
                        )}
                      </Typography>
                      {couponPreview.original_price != null ? (
                        <Box display="flex" alignItems="center" gap={1} mt={0.5} flexWrap="wrap">
                          <Typography variant="caption" color="text.secondary"
                            sx={{ textDecoration: 'line-through' }}>
                            {couponPreview.currency_code} {couponPreview.original_price?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </Typography>
                          <Typography variant="caption" color="error.main" fontWeight={700}>
                            −{couponPreview.currency_code} {couponPreview.discount_amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </Typography>
                          <Typography variant="body2" fontWeight={700} color="success.dark">
                            = {couponPreview.currency_code} {couponPreview.discounted_price?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                          {couponPreview.discount_type === 'percent'
                            ? `${couponPreview.discount_value}% off`
                            : `${couponPreview.currency_code} ${couponPreview.discount_value} off`}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Tooltip title="Remove coupon">
                    <IconButton size="small" onClick={removeCoupon}
                      sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <Box display="flex" gap={1.5} alignItems="flex-start">
                  <TextField size="small" label="Coupon Code" placeholder="e.g. LAUNCH50" sx={{ flex: 1 }}
                    value={couponInput}
                    onChange={e => { setCouponInput(e.target.value.toUpperCase().replace(/\s/g, '')); setCouponError(''); }}
                    onKeyDown={e => { if (e.key === 'Enter') applyCoupon(); }}
                    error={!!couponError} helperText={couponError || ''}
                    inputProps={{ style: { fontFamily: 'monospace', fontWeight: 700, letterSpacing: 1 } }}
                    InputProps={{ startAdornment: (
                      <InputAdornment position="start">
                        <LocalOfferOutlined sx={{ fontSize: 16, color: 'text.disabled' }} />
                      </InputAdornment>
                    )}} />
                  <Button variant="outlined" size="medium" onClick={applyCoupon}
                    disabled={!couponInput.trim() || couponApplying}
                    startIcon={couponApplying ? <CircularProgress size={14} /> : null}
                    sx={{ whiteSpace: 'nowrap', minWidth: 100, mt: 0.05 }}>
                    {couponApplying ? 'Checking…' : 'Apply'}
                  </Button>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>}

          {/* ── 4. Payment Details ────────────────────────────────────────── */}
          <Accordion disableGutters elevation={0} sx={ACCORDION_SX}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <SectionLabel label="Payment" />
                {payForm.mode && (
                  <Chip size="small"
                    label={PAY_MODES.find(m => m.value === payForm.mode)?.label || payForm.mode}
                    color={isPending ? 'warning' : 'success'}
                    sx={{ fontWeight: 700, fontSize: 11, height: 20 }} />
                )}
              </Box>
            </AccordionSummary>
            <Divider />
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" mb={2.5}>
                Select a payment mode to record payment. Leave unselected to assign without payment.
              </Typography>

              {/* Payment mode grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 1, mb: 2.5 }}>
                {PAY_MODES.map(m => (
                  <Box key={m.value}
                    onClick={() => setP('mode', payForm.mode === m.value ? '' : m.value)}
                    sx={{
                      border: '1px solid',
                      borderColor: payForm.mode === m.value ? 'primary.main' : 'divider',
                      bgcolor: payForm.mode === m.value ? 'primary.50' : 'background.paper',
                      borderRadius: 2, p: 1.25, textAlign: 'center', cursor: 'pointer',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: 'primary.300', bgcolor: 'primary.50' },
                    }}>
                    <CreditCard sx={{ fontSize: 20, color: payForm.mode === m.value ? 'primary.main' : 'text.disabled', mb: 0.5 }} />
                    <Typography variant="caption" display="block" fontWeight={600}
                      color={payForm.mode === m.value ? 'primary.main' : 'text.secondary'} lineHeight={1.2}>
                      {m.label}
                    </Typography>
                    <Chip size="small" label={m.online ? 'Online' : 'Offline'}
                      sx={{ fontSize: 9, height: 14, mt: 0.5,
                        bgcolor: m.online ? 'info.50' : 'grey.100',
                        color: m.online ? 'info.dark' : 'text.disabled',
                        '& .MuiChip-label': { px: 0.5 } }} />
                  </Box>
                ))}
              </Box>

              {payForm.mode && (
                <>
                  {/* Amount + currency + date */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 2, mb: 2 }}>
                    <TextField size="small" label="Amount *" type="number"
                      value={payForm.amount}
                      error={!!assignErrors.pay_amount}
                      helperText={assignErrors.pay_amount || (selectedPricing?.mrp_incl_tax ? `Plan price: ${selectedPricing.currency_code} ${selectedPricing.mrp_incl_tax}` : ' ')}
                      onChange={e => { setP('amount', e.target.value); if (assignErrors.pay_amount) setAssignErrors(p => ({ ...p, pay_amount: '' })); }}
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={NUM_NO_SPIN_SUB} />
                    <TextField size="small" label="Currency" value={payForm.currency_code}
                      onChange={e => setP('currency_code', e.target.value.toUpperCase().slice(0, 3))}
                      inputProps={{ maxLength: 3 }} sx={{ width: 100 }} />
                    <TextField size="small" label="Payment Date & Time" type="datetime-local" fullWidth
                      value={payForm.payment_date}
                      onChange={e => setP('payment_date', e.target.value)}
                      InputLabelProps={{ shrink: true }} />
                  </Box>

                  {/* Mode-specific reference fields */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                    {(isOnlineMode || payForm.mode === 'cash') && (
                      <TextField size="small" label="Reference Number"
                        value={payForm.reference_number}
                        onChange={e => setP('reference_number', e.target.value)}
                        placeholder="Bank ref / transaction ref" />
                    )}
                    {isOnlineMode && (
                      <TextField size="small" label="Transaction ID"
                        value={payForm.transaction_id}
                        onChange={e => setP('transaction_id', e.target.value)}
                        placeholder="Gateway transaction ID" />
                    )}
                    {payForm.mode === 'upi' && (
                      <TextField size="small" label="UTR Number"
                        value={payForm.utr_no}
                        onChange={e => setP('utr_no', e.target.value)}
                        placeholder="Unique Transaction Reference" />
                    )}
                    {payForm.mode === 'cheque' && (
                      <>
                        <TextField size="small" label="Cheque Number *"
                          value={payForm.cheque_no}
                          onChange={e => setP('cheque_no', e.target.value)} />
                        <TextField size="small" label="Reference Number"
                          value={payForm.reference_number}
                          onChange={e => setP('reference_number', e.target.value)} />
                      </>
                    )}
                    {(payForm.mode === 'cash' || payForm.mode === 'cheque') && (
                      <TextField size="small" label="Receipt Number"
                        value={payForm.receipt_no}
                        onChange={e => setP('receipt_no', e.target.value)} />
                    )}
                  </Box>

                  {/* Payment Status */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    {/* Status toggle */}
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary"
                        textTransform="uppercase" letterSpacing={0.5} display="block" mb={1.5}>
                        Payment Status
                      </Typography>
                      <Box display="flex" gap={1}>
                        {[
                          { val: 'captured', label: 'Payment Received', icon: <CheckCircleOutline sx={{ fontSize: 16 }} />, color: 'success' },
                          { val: 'pending',  label: 'Payment Pending',  icon: <PendingOutlined   sx={{ fontSize: 16 }} />, color: 'warning' },
                        ].map(s => (
                          <Box key={s.val}
                            onClick={() => setP('status', s.val)}
                            sx={{
                              flex: 1, display: 'flex', alignItems: 'center', gap: 1,
                              border: '1px solid',
                              borderColor: payForm.status === s.val ? `${s.color}.main` : 'divider',
                              bgcolor: payForm.status === s.val ? `${s.color}.50` : 'background.paper',
                              borderRadius: 2, px: 1.5, py: 1, cursor: 'pointer',
                              transition: 'all 0.15s',
                            }}>
                            <Box sx={{ color: payForm.status === s.val ? `${s.color}.main` : 'text.disabled' }}>
                              {s.icon}
                            </Box>
                            <Typography variant="caption" fontWeight={700}
                              color={payForm.status === s.val ? `${s.color}.dark` : 'text.secondary'}>
                              {s.label}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      {/* Pending: add credits now checkbox */}
                      {isPending && (
                        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                          <FormControlLabel
                            control={<Switch size="small" checked={payForm.add_tokens_now}
                              onChange={e => setP('add_tokens_now', e.target.checked)} color="warning" />}
                            label={
                              <Box>
                                <Typography variant="body2" fontWeight={600}>Add credits immediately</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {payForm.add_tokens_now
                                    ? 'Tokens granted now, even before payment confirms'
                                    : 'Tokens held — only added after marking payment received'}
                                </Typography>
                              </Box>
                            } />
                        </Box>
                      )}
                    </Box>

                    {/* Status info box */}
                    <Box sx={{
                      bgcolor: isPending ? 'warning.50' : 'success.50',
                      border: '1px solid',
                      borderColor: isPending ? 'warning.200' : 'success.200',
                      borderRadius: 2, p: 2,
                    }}>
                      {isPending ? (
                        <>
                          <Typography variant="body2" fontWeight={700} color="warning.dark" gutterBottom>
                            Payment Pending
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            • Subscription is created active<br />
                            • {payForm.add_tokens_now ? 'Credits added immediately' : 'Credits held until payment confirmed'}<br />
                            • Invoice generated once payment marked received
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography variant="body2" fontWeight={700} color="success.dark" gutterBottom>
                            Payment Received
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            • Subscription is created active<br />
                            • Credits added immediately<br />
                            • Invoice generated automatically
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </>
              )}
            </AccordionDetails>
          </Accordion>

          {/* ── 5. Upload Documents ───────────────────────────────────────── */}
          {payForm.mode && (
            <Accordion disableGutters elevation={0} sx={ACCORDION_SX}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <SectionLabel label="Payment Proofs" />
                  {attachments.filter(a => a.url).length > 0 && (
                    <Chip size="small"
                      label={`${attachments.filter(a => a.url).length} file${attachments.filter(a => a.url).length !== 1 ? 's' : ''}`}
                      color="primary" sx={{ fontWeight: 700, fontSize: 11, height: 20 }} />
                  )}
                </Box>
              </AccordionSummary>
              <Divider />
              <AccordionDetails sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Upload cheque copy, receipt screenshot, transaction proof or any supporting document.
                  <Typography component="span" variant="caption" color="text.disabled" ml={1}>
                    PDF / JPG / PNG · max 10 MB each
                  </Typography>
                </Typography>

                {/* Upload area */}
                <Box
                  component="label"
                  sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                    border: '2px dashed', borderColor: 'divider', borderRadius: 2,
                    py: 3, mb: 2, cursor: 'pointer',
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
                  }}>
                  <input type="file" hidden multiple accept=".pdf,.jpg,.jpeg,.png"
                    onChange={e => uploadFiles(e.target.files)} />
                  <AttachFileOutlined sx={{ fontSize: 28, color: 'text.disabled' }} />
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Click to upload or drag files here
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Cheque copy · Receipt · Transaction screenshot · Any supporting document
                  </Typography>
                </Box>

                {/* Attachment list */}
                {attachments.length > 0 && (
                  <Box display="flex" flexDirection="column" gap={1}>
                    {attachments.map(a => (
                      <Box key={a.uid} sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        border: '1px solid', borderColor: a.error ? 'error.200' : 'divider',
                        bgcolor: a.error ? 'error.50' : 'grey.50',
                        borderRadius: 1.5, px: 1.5, py: 1,
                      }}>
                        <InsertDriveFileOutlined sx={{ fontSize: 18, color: a.error ? 'error.main' : 'text.disabled' }} />
                        <Box flex={1} overflow="hidden">
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {a.file.name}
                          </Typography>
                          {a.uploading && <LinearProgress sx={{ mt: 0.5, height: 2, borderRadius: 1 }} />}
                          {a.error && <Typography variant="caption" color="error.main">{a.error}</Typography>}
                          {a.url && <Typography variant="caption" color="success.main">✓ Uploaded</Typography>}
                        </Box>
                        <IconButton size="small" color="error"
                          onClick={() => setAttachments(arr => arr.filter(x => x.uid !== a.uid))}>
                          <DeleteOutline sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          )}

          {/* ── 6. Notes ──────────────────────────────────────────────────── */}
          <Accordion disableGutters elevation={0} sx={ACCORDION_SX}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
              <SectionLabel label="Internal Notes" />
            </AccordionSummary>
            <Divider />
            <AccordionDetails sx={{ p: 3 }}>
              <TextField label="Notes (optional)" size="small" fullWidth multiline rows={3}
                value={form.admin_notes}
                placeholder="Internal notes about this subscription or payment…"
                onChange={e => setForm(f => ({ ...f, admin_notes: e.target.value }))} />
            </AccordionDetails>
          </Accordion>

        </Box>

        <Snackbar open={snack.open} autoHideDuration={4000}
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
        </Snackbar>
      </Box>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // UPGRADE VIEW
  // ══════════════════════════════════════════════════════════════════════════
  if (view === 'upgrade' && upgradeSub) {
    const upCurrency = upgradeSub?.merchant_country_name === 'India' ? 'INR' : ((upgradeSub?.merchant_mobile_code || '').trim() === '+91' ? 'INR' : (upgradeSub?.currency_code || 'INR'));
    const isUpOnline = ['credit_card','debit_card','netbanking','upi'].includes(upgradePayForm.mode);
    const isUpPending = upgradePayForm.status === 'pending';

    return (
      <Box sx={{ p: 3 }}>

        {/* Sticky header */}
        <Box display="flex" justifyContent="space-between" alignItems="center"
          sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: 'background.paper',
            borderBottom: '1px solid', borderColor: 'divider',
            py: 1.5, mb: 3, mx: -3, px: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <Box display="
          flex" alignItems="center" gap={1.5}>
            <Tooltip title="Back to list">
              <IconButton onClick={() => { setView('list'); setUpgradeSub(null); }} size="small"
                sx={{ border: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'grey.100' } }}>
                <ArrowBack fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2} letterSpacing={-0.3}>
                Upgrade Subscription
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {upgradeSub.merchant_name}{upgradeSub.merchant_email && ` · ${upgradeSub.merchant_email}`} · Current: <strong>{upgradeSub.plan_name || '—'}</strong> · Expires {upgradeSub.expiry_date?.slice(0,10)}
                {upgradeSub.remaining_tokens > 0 && (
                  <Box component="span" sx={{ ml: 1, px: 1, py: 0.2, bgcolor: '#fef9c3', color: '#92400e', borderRadius: 1, fontSize: 11, fontWeight: 600 }}>
                    {upgradeSub.remaining_tokens.toLocaleString('en-IN')} tokens → carry forward
                  </Box>
                )}
              </Typography>
            </Box>
          </Box>
          <Button variant="contained" color="secondary"
            startIcon={upgrading ? <CircularProgress size={14} color="inherit" /> : <UpgradeIcon />}
            onClick={submitUpgrade} disabled={upgrading || !upgradeForm.plan_version_id || !upgradeForm.expiry_date}
            sx={{ borderRadius: 2, fontWeight: 700, minWidth: 180, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
            {upgrading ? 'Upgrading…' : (upgradePayForm.mode ? 'Upgrade & Record Payment' : 'Confirm Upgrade')}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Policy alert */}
          <Alert severity="info" sx={{ borderRadius: 2, fontSize: 13 }}>
            Upgrading will <strong>immediately cancel</strong> the current plan. Unused tokens carry forward and expire at <strong>new plan expiry + 90 days</strong>. Billed at <strong>full price</strong>, no proration.
          </Alert>

          {/* Carry-forward preview */}
          {upgradeSub.remaining_tokens > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 2 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>↻</Box>
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#15803d' }}>
                  {upgradeSub.remaining_tokens.toLocaleString('en-IN')} tokens will carry forward
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#166534' }}>
                  {upgradeForm.expiry_date ? (() => { const g = new Date(upgradeForm.expiry_date); g.setDate(g.getDate()+90); return `Valid until ${g.toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})} (new plan expiry + 90 days)`; })() : 'Set expiry date to see carry-forward expiry'}
                </Typography>
              </Box>
            </Box>
          )}

          {/* 1. New Plan & Dates */}
          <Accordion defaultExpanded disableGutters elevation={0} sx={ACCORDION_SX}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <SectionLabel label="New Plan & Dates" />
                {upgradeSelectedPlan && (
                  <Chip size="small" label={upgradeSelectedPlan.display_name || upgradeSelectedPlan.plan_name} color="secondary"
                    sx={{ fontWeight: 700, fontSize: 11, height: 20 }} />
                )}
              </Box>
            </AccordionSummary>
            <Divider />
            <AccordionDetails sx={{ p: 3 }}>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Plan *</InputLabel>
                  <Select label="Plan *" value={upgradeForm.plan_version_id}
                    onChange={e => {
                      const pv = planVersions.find(p => p.plan_version_id === Number(e.target.value));
                      setUpgradeSelectedPlan(pv || null);
                      const first = pv?.pricing?.find(p => p.currency_code === upCurrency) || pv?.pricing?.[0];
                      const period = first?.billing_period || '';
                      setUpgradeForm(f => ({ ...f, plan_version_id: e.target.value, billing_period: period, bundled_tokens: first?.bundled_tokens ?? '', expiry_date: calcExpiry(f.start_date, period) }));
                      setUP('currency_code', upCurrency);
                      if (first?.mrp_excl_tax) setUP('amount', first.mrp_excl_tax);
                    }}>
                    {(() => {
                      const cur = planVersions.find(x => x.plan_version_id === upgradeSub?.plan_version_id || x.plan_code === upgradeSub?.plan_code);
                      const curTier = cur?.plan_tier ?? -1;
                      return planVersions
                        .filter(p => p.plan_code !== upgradeSub?.plan_code)
                        .filter(p => { if (!upgradeSub?.currency_code) return true; const cs = (p.pricing||[]).map(r=>r.currency_code); return cs.length===0||cs.includes(upgradeSub.currency_code); })
                        .filter(p => curTier < 0 || (p.plan_tier ?? 0) > curTier)
                        .map(p => (
                          <MenuItem key={p.plan_version_id} value={p.plan_version_id}>
                            {p.display_name}{p.plan_tier > 0 && <Box component="span" sx={{ ml:1, fontSize:10, color:'#9ca3af' }}>Tier {p.plan_tier}</Box>}
                          </MenuItem>
                        ));
                    })()}
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth disabled={!upgradeForm.plan_version_id}>
                  <InputLabel>Billing Period</InputLabel>
                  <Select label="Billing Period" value={upgradeForm.billing_period}
                    onChange={e => {
                      const pricing = upgradeSelectedPlan?.pricing?.find(x => x.billing_period === e.target.value);
                      setUpgradeForm(f => ({ ...f, billing_period: e.target.value, bundled_tokens: pricing?.bundled_tokens ?? f.bundled_tokens, expiry_date: calcExpiry(f.start_date, e.target.value) }));
                      if (pricing?.mrp_excl_tax) setUP('amount', pricing.mrp_excl_tax);
                    }}>
                    {(upgradeSelectedPlan?.pricing||[]).filter(p=>p.currency_code===upCurrency).map(p=>(
                      <MenuItem key={p.billing_period} value={p.billing_period}>
                        {p.billing_period.replace(/_/g,' ')} — {p.currency_code} {Number(p.mrp_excl_tax).toLocaleString('en-IN')} excl. GST
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField label="Start date" type="date" size="small" fullWidth
                  value={upgradeForm.start_date}
                  onChange={e => setUpgradeForm(f => ({ ...f, start_date: e.target.value, expiry_date: calcExpiry(e.target.value, f.billing_period) || f.expiry_date }))}
                  InputLabelProps={{ shrink: true }} />
                <TextField label="Expiry date *" type="date" size="small" fullWidth
                  value={upgradeForm.expiry_date}
                  onChange={e => setUpgradeForm(f => ({ ...f, expiry_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  helperText={upgradeForm.billing_period ? `Auto-filled from start + ${upgradeForm.billing_period}` : ' '} />
                <TextField label="Bundled tokens" type="number" size="small" fullWidth
                  value={upgradeForm.bundled_tokens}
                  onChange={e => setUpgradeForm(f => ({ ...f, bundled_tokens: e.target.value }))} />
              </Box>

              {upgradeSelectedPlan && upgradeForm.billing_period && (() => {
                const p = upgradeSelectedPlan.pricing?.find(x => x.billing_period === upgradeForm.billing_period);
                if (!p) return null;
                return (
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f3ff', border: '1px solid #e9d5ff', borderRadius: 2, display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 2 }}>
                    {[['Plan', upgradeSelectedPlan.display_name||upgradeSelectedPlan.plan_name],['Period',p.billing_period.replace(/_/g,' ')],['Price (excl. GST)',`${p.currency_code} ${Number(p.mrp_excl_tax).toLocaleString('en-IN')}`],['Bundled Tokens',Number(p.bundled_tokens).toLocaleString('en-IN')]].map(([label,val])=>(
                      <Box key={label}>
                        <Typography sx={{ fontSize:10,color:'#7c3aed',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.05em',mb:0.25 }}>{label}</Typography>
                        <Typography sx={{ fontSize:13,fontWeight:700,color:'#4c1d95' }}>{val}</Typography>
                      </Box>
                    ))}
                  </Box>
                );
              })()}
            </AccordionDetails>
          </Accordion>

          {/* 2. Coupon */}
          <Accordion disableGutters elevation={0} sx={ACCORDION_SX}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
              <SectionLabel label="Coupon (optional)" />
            </AccordionSummary>
            <Divider />
            <AccordionDetails sx={{ p: 3 }}>
              <TextField size="small" fullWidth placeholder="Enter coupon code"
                value={upgradeCoupon} onChange={e => setUpgradeCoupon(e.target.value.toUpperCase())}
                InputProps={{ sx: { fontFamily: "'Roboto Mono', monospace", letterSpacing: '0.08em', fontSize: 13 } }} />
            </AccordionDetails>
          </Accordion>

          {/* 3. Payment */}
          <Accordion disableGutters elevation={0} sx={ACCORDION_SX}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <SectionLabel label="Payment (optional)" />
                {upgradePayForm.mode && (
                  <Chip size="small"
                    label={isUpPending ? 'Pending' : 'Received'}
                    color={isUpPending ? 'warning' : 'success'}
                    sx={{ fontWeight: 700, fontSize: 11, height: 20 }} />
                )}
              </Box>
            </AccordionSummary>
            <Divider />
            <AccordionDetails sx={{ p: 3 }}>
              {/* Mode chips */}
              <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                {PAY_MODES.map(({ value, label }) => (
                  <Chip key={value} label={label} size="small" clickable
                    variant={upgradePayForm.mode === value ? 'filled' : 'outlined'}
                    color={upgradePayForm.mode === value ? 'primary' : 'default'}
                    onClick={() => setUP('mode', upgradePayForm.mode === value ? '' : value)}
                    sx={{ borderRadius: 1.5, '& .MuiChip-label': { px: 0.5 } }} />
                ))}
              </Box>

              {upgradePayForm.mode && (
                <>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 2, mb: 2 }}>
                    <TextField size="small" label="Amount *" type="number"
                      value={upgradePayForm.amount} onChange={e => setUP('amount', e.target.value)}
                      inputProps={{ min: 0, step: 0.01 }} sx={NUM_NO_SPIN_SUB} />
                    <TextField size="small" label="Currency" value={upgradePayForm.currency_code}
                      onChange={e => setUP('currency_code', e.target.value.toUpperCase().slice(0,3))}
                      inputProps={{ maxLength: 3 }} sx={{ width: 100 }} />
                    <TextField size="small" label="Payment Date & Time" type="datetime-local" fullWidth
                      value={upgradePayForm.payment_date} onChange={e => setUP('payment_date', e.target.value)}
                      InputLabelProps={{ shrink: true }} />
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                    {(isUpOnline || upgradePayForm.mode === 'cash') && (
                      <TextField size="small" label="Reference Number" value={upgradePayForm.reference_number}
                        onChange={e => setUP('reference_number', e.target.value)} placeholder="Bank ref / transaction ref" />
                    )}
                    {isUpOnline && (
                      <TextField size="small" label="Transaction ID" value={upgradePayForm.transaction_id}
                        onChange={e => setUP('transaction_id', e.target.value)} placeholder="Gateway transaction ID" />
                    )}
                    {upgradePayForm.mode === 'upi' && (
                      <TextField size="small" label="UTR Number" value={upgradePayForm.utr_no}
                        onChange={e => setUP('utr_no', e.target.value)} placeholder="Unique Transaction Reference" />
                    )}
                    {upgradePayForm.mode === 'cheque' && (
                      <>
                        <TextField size="small" label="Cheque Number *" value={upgradePayForm.cheque_no}
                          onChange={e => setUP('cheque_no', e.target.value)} />
                        <TextField size="small" label="Reference Number" value={upgradePayForm.reference_number}
                          onChange={e => setUP('reference_number', e.target.value)} />
                      </>
                    )}
                    {(upgradePayForm.mode === 'cash' || upgradePayForm.mode === 'cheque') && (
                      <TextField size="small" label="Receipt Number" value={upgradePayForm.receipt_no}
                        onChange={e => setUP('receipt_no', e.target.value)} />
                    )}
                  </Box>

                  {/* Payment Status */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary"
                        textTransform="uppercase" letterSpacing={0.5} display="block" mb={1.5}>
                        Payment Status
                      </Typography>
                      <Box display="flex" gap={1}>
                        {[
                          { val: 'captured', label: 'Payment Received', icon: <CheckCircleOutline sx={{ fontSize: 16 }} />, color: 'success' },
                          { val: 'pending',  label: 'Payment Pending',  icon: <PendingOutlined   sx={{ fontSize: 16 }} />, color: 'warning' },
                        ].map(s => (
                          <Box key={s.val} onClick={() => setUP('status', s.val)}
                            sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, border: '1px solid',
                              borderColor: upgradePayForm.status === s.val ? `${s.color}.main` : 'divider',
                              bgcolor: upgradePayForm.status === s.val ? `${s.color}.50` : 'background.paper',
                              borderRadius: 2, px: 1.5, py: 1, cursor: 'pointer', transition: 'all 0.15s' }}>
                            <Box sx={{ color: upgradePayForm.status === s.val ? `${s.color}.main` : 'text.disabled' }}>{s.icon}</Box>
                            <Typography variant="caption" fontWeight={700}
                              color={upgradePayForm.status === s.val ? `${s.color}.dark` : 'text.secondary'}>
                              {s.label}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      {isUpPending && (
                        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                          <FormControlLabel
                            control={<Switch size="small" checked={upgradePayForm.add_tokens_now}
                              onChange={e => setUP('add_tokens_now', e.target.checked)} color="warning" />}
                            label={
                              <Box>
                                <Typography variant="body2" fontWeight={600}>Add credits immediately</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {upgradePayForm.add_tokens_now
                                    ? 'Tokens granted now, even before payment confirms'
                                    : 'Tokens held — only added after marking payment received'}
                                </Typography>
                              </Box>
                            } />
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ bgcolor: isUpPending ? 'warning.50' : 'success.50', border: '1px solid',
                      borderColor: isUpPending ? 'warning.200' : 'success.200', borderRadius: 2, p: 2 }}>
                      {isUpPending ? (
                        <>
                          <Typography variant="body2" fontWeight={700} color="warning.dark" gutterBottom>Payment Pending</Typography>
                          <Typography variant="caption" color="text.secondary">
                            • Subscription is created active<br />
                            • {upgradePayForm.add_tokens_now ? 'Credits added immediately' : 'Credits held until payment confirmed'}<br />
                            • Invoice generated once payment marked received
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography variant="body2" fontWeight={700} color="success.dark" gutterBottom>Payment Received</Typography>
                          <Typography variant="caption" color="text.secondary">
                            • Subscription is created active<br />
                            • Credits added immediately<br />
                            • Invoice generated automatically
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </>
              )}
            </AccordionDetails>
          </Accordion>

          {/* 4. Internal Notes */}
          <Accordion disableGutters elevation={0} sx={ACCORDION_SX}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
              <SectionLabel label="Internal Notes" />
            </AccordionSummary>
            <Divider />
            <AccordionDetails sx={{ p: 3 }}>
              <TextField label="Notes (optional)" size="small" fullWidth multiline rows={3}
                value={upgradeForm.admin_notes}
                placeholder="Internal notes about this upgrade or payment…"
                onChange={e => setUpgradeForm(f => ({ ...f, admin_notes: e.target.value }))} />
            </AccordionDetails>
          </Accordion>

        </Box>

        <Snackbar open={snack.open} autoHideDuration={4000}
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
        </Snackbar>
      </Box>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // LIST VIEW
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <Box sx={{ p: 3 }}>

      {/* Page header — sticky */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start"
        mb={3} pb={2.5}
        sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: 'background.paper',
          borderBottom: '1px solid', borderColor: 'divider',
          mx: -3, px: 3, pt: 2.5,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        <Box>
          <Typography variant="h5" fontWeight={800} letterSpacing={-0.5} gutterBottom>
            Subscriptions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Assign and manage merchant subscriptions, token balances, and expiry.
          </Typography>
        </Box>
        <Box display="flex" gap={1.5} alignItems="center" mt={0.5}>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={loadItems}
              sx={{ border: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'grey.50' } }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button size="small" variant="outlined" startIcon={<AuditIcon />}
            onClick={() => navigate('/glam-admin/subscriptions_v2/audit-logs')}
            sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', whiteSpace: 'nowrap' }}>
            Audit Logs
          </Button>
          <Button size="small" variant="outlined" startIcon={<FileDownloadOutlined />} onClick={handleExport}
            sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', whiteSpace: 'nowrap' }}>
            Export CSV
          </Button>
          {canAssign && (
            <Button variant="contained" size="small" startIcon={<Add />} onClick={openAssign}
              sx={{ borderRadius: 2, fontWeight: 700, whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.2)' } }}>
              Assign Subscription
            </Button>
          )}
        </Box>
      </Box>

      {/* Filters — same pattern as PlansManagement */}
      <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2.5, overflow: 'hidden' }}>
        <Box display="flex" gap={1.5} px={2} py={1.5} alignItems="center" flexWrap="wrap"
          sx={{ bgcolor: 'grey.50' }}>
          <TuneRounded sx={{ fontSize: 18, color: 'text.disabled' }} />

          <TextField size="small" placeholder="Search by name, email, mobile, WhatsApp or company…" value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ width: 260, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 17, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }} />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusFilter}
              onChange={e => setStatus(e.target.value)}
              sx={{ borderRadius: 2, bgcolor: 'white' }}>
              <MenuItem value="">All statuses</MenuItem>
              {['active','suspended','grace','expired','cancelled'].map(s => (
                <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {(search || statusFilter) && (
            <Button size="small" onClick={() => { setSearch(''); setStatus(''); }}
              sx={{ color: 'error.main', fontWeight: 600, fontSize: 12, '&:hover': { bgcolor: 'error.50' } }}>
              Clear filters
            </Button>
          )}

          <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
            {listLoading ? 'Loading…' : `${total} subscription${total !== 1 ? 's' : ''}`}
          </Typography>
        </Box>
      </Paper>

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...TH, pl: 2.5, width: 40 }}>#</TableCell>
                <TableCell sx={TH}>Merchant</TableCell>
                <TableCell sx={TH}>Plan / Add-on</TableCell>
                <TableCell sx={{ ...TH, width: 80 }}>Type</TableCell>
                <TableCell sx={{ ...TH, width: 110 }}>
                  <SortTH label="Status" field="status" sortBy={sortBy} sortDir={sortDir} onSort={col => { setSortBy(col); setSortDir(d => col === sortBy ? (d === 'asc' ? 'desc' : 'asc') : 'desc'); }} />
                </TableCell>
                <TableCell sx={{ ...TH, width: 105 }}>
                  <SortTH label="Start" field="start_date" sortBy={sortBy} sortDir={sortDir} onSort={col => { setSortBy(col); setSortDir(d => col === sortBy ? (d === 'asc' ? 'desc' : 'asc') : 'desc'); }} />
                </TableCell>
                <TableCell sx={{ ...TH, width: 115 }}>
                  <SortTH label="Expiry" field="expiry_date" sortBy={sortBy} sortDir={sortDir} onSort={col => { setSortBy(col); setSortDir(d => col === sortBy ? (d === 'asc' ? 'desc' : 'asc') : 'desc'); }} />
                </TableCell>
                <TableCell sx={{ ...TH, width: 100 }}>Tokens</TableCell>
                <TableCell sx={{ ...TH, width: 130, textAlign: 'right', pr: 2 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 9 }).map((__, j) => (
                        <TableCell key={j} sx={{ py: 1.5 }}><Skeleton height={20} /></TableCell>
                      ))}
                    </TableRow>
                  ))
                : items.length === 0
                ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 8, border: 0 }}>
                      <Typography variant="body1" fontWeight={600} color="text.secondary" mb={0.5}>
                        {search || statusFilter ? 'No results' : 'No subscriptions yet'}
                      </Typography>
                      <Typography variant="body2" color="text.disabled" mb={2}>
                        {search || statusFilter
                          ? 'Try adjusting your search or status filter'
                          : 'Assign a subscription to a merchant to get started'}
                      </Typography>
                      {!search && !statusFilter && canAssign && (
                        <Button variant="outlined" startIcon={<Add />} onClick={openAssign} size="small"
                          sx={{ borderRadius: 2, fontWeight: 600 }}>
                          Assign first subscription
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                )
                : items.map((sub, i) => {
                  const daysToExpiry = sub.expiry_date
                    ? Math.ceil((new Date(sub.expiry_date) - new Date()) / 86400000)
                    : null;
                  const expiringColor = daysToExpiry !== null && daysToExpiry <= 7 && sub.status === 'active'
                    ? 'error.main' : daysToExpiry !== null && daysToExpiry <= 30 && sub.status === 'active'
                    ? 'warning.main' : 'text.secondary';

                  return (
                    <TableRow key={sub.id} hover
                      sx={{ transition: 'background 0.12s' }}>

                      {/* # */}
                      <TableCell sx={{ pl: 2.5, color: 'text.disabled', fontWeight: 500, fontSize: 13 }}>
                        {page * perPage + i + 1}
                      </TableCell>

                      {/* Merchant */}
                      <TableCell sx={{ py: 1.5, maxWidth: 200 }}>
                        <Tooltip title={sub.merchant_name} placement="top" arrow>
                          <Typography variant="body2" fontWeight={600}
                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {sub.merchant_name}
                          </Typography>
                        </Tooltip>
                        <Tooltip title={sub.merchant_email} placement="bottom" arrow>
                          <Typography variant="caption" color="text.disabled"
                            fontFamily="'Roboto Mono', monospace" fontSize={11}
                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                            {sub.merchant_email}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      {/* Plan / Add-on */}
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Tooltip title={sub.plan_name || ''} placement="top" arrow>
                          <Typography variant="body2" fontWeight={600}
                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {sub.plan_name || '—'}
                          </Typography>
                        </Tooltip>
                        <Box display="flex" alignItems="center" gap={0.5} mt={0.25}>
                          {sub.plan_code && (
                            <Typography variant="caption" color="text.disabled"
                              fontFamily="'Roboto Mono', monospace" fontSize={10}>
                              {sub.plan_code}
                            </Typography>
                          )}
                          {sub.version_number != null && (
                            <Chip size="small" label={`v${sub.version_number}`}
                              sx={{ height: 16, fontSize: 10, fontWeight: 700,
                                bgcolor: 'grey.100', color: 'text.secondary',
                                '& .MuiChip-label': { px: 0.8 } }} />
                          )}
                        </Box>
                      </TableCell>

                      {/* Type */}
                      <TableCell>
                        <Chip size="small"
                          label={sub.sub_type === 'addon' ? 'Add-on' : 'Plan'}
                          sx={{ height: 20, fontSize: 10, fontWeight: 700,
                            bgcolor: sub.sub_type === 'addon' ? 'secondary.50' : 'primary.50',
                            color: sub.sub_type === 'addon' ? 'secondary.dark' : 'primary.dark',
                            border: '1px solid',
                            borderColor: sub.sub_type === 'addon' ? 'secondary.200' : 'primary.200',
                            '& .MuiChip-label': { px: 1 } }} />
                      </TableCell>

                      {/* Status */}
                      <TableCell><StatusChip status={sub.status} /></TableCell>

                      {/* Start */}
                      <TableCell>
                        <Typography variant="body2" fontSize={12} color="text.secondary">
                          {fmt(sub.start_date)}
                        </Typography>
                      </TableCell>

                      {/* Expiry */}
                      <TableCell>
                        <Typography variant="body2" fontSize={12} fontWeight={daysToExpiry !== null && daysToExpiry <= 30 ? 600 : 400}
                          color={expiringColor}>
                          {fmt(sub.expiry_date)}
                        </Typography>
                        {daysToExpiry !== null && daysToExpiry <= 30 && sub.status === 'active' && (
                          <Typography variant="caption" fontSize={10}
                            color={daysToExpiry <= 7 ? 'error.main' : 'warning.main'}>
                            {daysToExpiry <= 0 ? 'Expired' : `${daysToExpiry}d left`}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Tokens */}
                      <TableCell>
                        {sub.remaining_tokens === 0
                          ? <Chip label="Empty" size="small"
                              sx={{ fontWeight: 700, fontSize: 11, height: 20,
                                bgcolor: 'error.50', color: 'error.dark',
                                border: '1px solid', borderColor: 'error.200',
                                '& .MuiChip-label': { px: 1 } }} />
                          : <Box>
                              <Typography variant="body2" fontWeight={700}
                                color={sub.remaining_tokens < 100 ? 'warning.dark' : 'text.primary'}>
                                {(sub.remaining_tokens || 0).toLocaleString()}
                              </Typography>
                              {sub.remaining_tokens < 100 && sub.remaining_tokens > 0 && (
                                <Typography variant="caption" fontSize={10} color="warning.main">Low</Typography>
                              )}
                            </Box>
                        }
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right" sx={{ pr: 2 }}>
                        <Box display="flex" gap={0.5} justifyContent="flex-end">
                          {sub.status === 'active' && canSuspend && (
                            <Tooltip title="Suspend">
                              <IconButton size="small" onClick={() => toggleSuspend(sub)} sx={BTN_WARNING}>
                                <SuspendIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {sub.status === 'suspended' && canReactivate && (
                            <Tooltip title="Reactivate">
                              <IconButton size="small" onClick={() => toggleSuspend(sub)} sx={BTN_SUCCESS}>
                                <ReactivateIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {sub.status === 'active' && sub.sub_type === 'plan' && canAssign && (
                            <Tooltip title="Upgrade plan">
                              <IconButton size="small" onClick={() => openUpgrade(sub)} sx={BTN_SECONDARY}>
                                <UpgradeIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {sub.status !== 'cancelled' && canExtend && (
                            <Tooltip title="Extend expiry">
                              <IconButton size="small" sx={BTN_PRIMARY}
                                onClick={() => { setExtendSub(sub); setExtendForm({ expiry_date: sub.expiry_date?.slice(0,10) || '', reason: '' }); }}>
                                <ExtendIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {sub.status !== 'cancelled' && canCancel && (
                            <Tooltip title="Cancel">
                              <IconButton size="small" sx={BTN_ERROR}
                                onClick={() => { setCancelSub(sub); setCancelReason(''); }}>
                                <CancelIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Token ledger">
                            <IconButton size="small" onClick={() => openLedger(sub)} sx={BTN_GREY}>
                              <LedgerIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              }
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={perPage}
        onRowsPerPageChange={e => { setPerPage(+e.target.value); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50]}
        sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 0.5 }}
      />

      {/* ── Extend Dialog ── */}
      <Dialog open={Boolean(extendSub)} onClose={() => setExtendSub(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Extend Subscription</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{extendSub?.merchant_name}</strong> — current expiry:{' '}
            <strong>{fmt(extendSub?.expiry_date)}</strong>
          </Typography>
          <TextField label="New expiry date *" type="date" size="small" fullWidth
            value={extendForm.expiry_date}
            error={!!extendErrors.expiry_date}
            helperText={extendErrors.expiry_date || ' '}
            onChange={e => { setExtendForm(f => ({ ...f, expiry_date: e.target.value })); if (extendErrors.expiry_date) setExtendErrors({}); }}
            InputLabelProps={{ shrink: true }} />
          <TextField label="Reason (optional)" size="small" multiline rows={2}
            value={extendForm.reason}
            onChange={e => setExtendForm(f => ({ ...f, reason: e.target.value }))} />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setExtendSub(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitExtend} disabled={extending}
            sx={{ borderRadius: 2, fontWeight: 700, minWidth: 100 }}>
            {extending ? <CircularProgress size={16} /> : 'Extend'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Cancel Dialog ── */}
      <Dialog open={Boolean(cancelSub)} onClose={() => setCancelSub(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Cancel Subscription</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            This will cancel <strong>{cancelSub?.merchant_name}</strong>'s subscription. This action cannot be undone.
          </Alert>
          <TextField label="Reason (optional)" size="small" multiline rows={2}
            value={cancelReason} onChange={e => setCancelReason(e.target.value)} />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setCancelSub(null)}>Back</Button>
          <Button variant="contained" color="error" onClick={submitCancel} disabled={cancelling}
            sx={{ borderRadius: 2, fontWeight: 700, minWidth: 140 }}>
            {cancelling ? <CircularProgress size={16} /> : 'Confirm Cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Token Ledger Dialog ── */}
      <Dialog open={Boolean(ledgerSub)} onClose={() => setLedgerSub(null)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700 }}>
          <Box>
            <Typography fontWeight={700}>Token Ledger</Typography>
            <Typography variant="caption" color="text.secondary">{ledgerSub?.merchant_name}</Typography>
          </Box>
          <IconButton size="small" onClick={() => setLedgerSub(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0 }}>
          {ledgerLoading
            ? <Box sx={{ p: 3 }}>{[1,2,3].map(i => <Skeleton key={i} height={36} sx={{ mb: 1 }} />)}</Box>
            : ledger.length === 0
            ? <Box sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>No ledger entries</Box>
            : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Date', 'Type', 'Tokens', 'Balance After', 'Description'].map(h => (
                        <TableCell key={h} sx={{ ...TH, py: 1.5 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ledger.map(e => (
                      <TableRow key={e.id} hover>
                        <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{fmt(e.created_at)}</TableCell>
                        <TableCell>
                          <Chip label={e.txn_type.replace(/_/g, ' ')} size="small" variant="outlined"
                            color={e.tokens > 0 ? 'success' : 'error'}
                            sx={{ fontSize: 11, '& .MuiChip-label': { px: 1 } }} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: e.tokens > 0 ? 'success.main' : 'error.main' }}>
                          {e.tokens > 0 ? '+' : ''}{e.tokens}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{(e.balance_after || 0).toLocaleString()}</TableCell>
                        <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{e.description || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          }
        </DialogContent>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
